/* eslint-disable react-hooks/exhaustive-deps */
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PublishIcon from '@mui/icons-material/Publish';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import { Box } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CURRENCY } from '../../../config/appConfig';
import { AppRoutes } from "../../../constants/appRoutes";
import { LoanStatuses } from '../../../constants/loanStatus';
import { FormHelper } from "../../../helpers/formHelper";
import { buildRegionizeRoute } from "../../../helpers/regionRouteHelper";
import ActionBarDetail from "../../../shared/components/common/actionBarDetail";
import SectionWithHeading from "../../../shared/components/common/sectionWithHeading";
import { Spacing } from "../../../shared/components/common/spacing";
import ButtonWrapper from "../../../shared/formUtilities/useButton";
import DatePickerWrapper from "../../../shared/formUtilities/useDatePicker";
import InputWrapper from "../../../shared/formUtilities/useInput";
import SelectWrapper from "../../../shared/formUtilities/useSelect";
import { PromoCodeSchema } from "../../../shared/validations/schema";
import { LoanService } from '../../loans/loanService';
import { PromotionalCodeService } from "../promotionalCodeService";
const CouponType = {
  Fixed: 1,
  Percentage: 2
};

const OpportinityType = {
  All: 1,
  Custom: 2
};

const ApplicableOpportinities = [
  { name: 'All', displayName: "All", id: OpportinityType.All },
  { name: 'Specific', displayName: "Specific", id: OpportinityType.Custom }
];

export const PromotionalCodeCreate = () => {
  const promotionalCodeService = new PromotionalCodeService();
  const loanService = new LoanService();
  const [loading, setLoading] = useState(false);
  const [loans, setLoans] = useState([]);
  const [codeDetail, setCodeDetail] = useState(null);
  const [maxCashbackError, setMaxCashbackError] = useState(false);
  const id = useParams()?.id;
  const navigate = useNavigate();
  const form = useFormik({
    initialValues: {
      code: null,
      type: CouponType.Fixed,
      startDate: null,
      expiryDate: null,
      maxUsagePerUser: null,
      maxTotalUsage: null,
      loanApplicable: OpportinityType.All,
      isActive: false,
      promoCodeLoans: [],
      promoCodeRanges: [
        { toAmount: null, fromAmount: null, value: null, maximumCashbackAmount: null }
      ]
    },
    onSubmit: () => {
      onSubmitCode();
    },
    validationSchema: PromoCodeSchema,
    ...FormHelper.formikProps()
  });

  useEffect(() => {
    getLoanAndPromoItems();
  }, [id]);

  const getLoanAndPromoItems = async () => {
    try {
      setLoading(true);
      let resp = await getFundraisingLoans();
      let loans = [];
      if (resp.data.loans && resp.data.loans?.length) {
        loans = resp.data.loans.map(resp => {
          return {
            id: resp.id,
            name: resp.businessName,
            displayName: resp.businessName,
            businessName: resp.businessName,
            loanStatus: resp.loanStatus,
          };
        });
      }
      setLoans(loans);
      if (id) {
        getCodeDetails(loans);
      } else {
        setLoading(false);
      }
    }
    catch { setLoading(false); }
  };

  const formatThreeDecimalsWithoutRounding = (num) => {
    return Math.trunc(num * 10000) / 10000;
  };

  const getCodeDetails = (loanValues = loans) => {
    setLoading(true);
    promotionalCodeService.getCodeDetail(id).then(resp => {
      let data = resp.data;
      let tempPromoCodeLoans = [];
      data["loanApplicable"] = OpportinityType.All;
      if (data?.promoCodeLoans && data?.promoCodeLoans?.length) {
        data["loanApplicable"] = OpportinityType.Custom;
        let promoCodeLoans = [];
        data.promoCodeLoans.forEach(resp => {
          promoCodeLoans.push(resp?.loanId);
          tempPromoCodeLoans.push({ id: resp.id, loanId: resp.loanId });
        });
        data.promoCodeLoans = promoCodeLoans;
      }

      data.promoCodeRanges.forEach(resp => {
        resp.value = data.type === CouponType.Percentage ? resp.value ? formatThreeDecimalsWithoutRounding((parseFloat(resp.value) * 100)) : 0 : parseFloat(resp.value);
        resp.toAmount = parseFloat(resp.toAmount) === 0 ? '0' : parseFloat(resp.toAmount) || null;
        resp.fromAmount = parseFloat(resp.fromAmount) === 0 ? '0' : parseFloat(resp.fromAmount) || null;
        resp.maximumCashbackAmount = parseFloat(resp.maximumCashbackAmount) === 0 ? '0' : parseFloat(resp.maximumCashbackAmount) || null;
      });
      setCodeDetail({ ...data, tempPromoCodeLoans: tempPromoCodeLoans });
      form.setValues({ ...data });
      setLoading(false);
    }).catch(err => { setLoading(false); });
  };

  const getLoanDetails = (id) => {
    if (loans?.length) {
      return loans.find(resp => resp.id === id);
    }
    return {};
  };

  const getLoanId = (id) => {
    if (codeDetail && codeDetail?.tempPromoCodeLoans && codeDetail?.tempPromoCodeLoans?.length) {
      return codeDetail?.tempPromoCodeLoans.find(resp => resp.loanId === id);
    }
    return null;
  };

  const onSubmitCode = () => {
    if (!checkError()) {
      let formValues = JSON.parse(JSON.stringify(form.values));

      if (formValues.promoCodeLoans && formValues.promoCodeLoans?.length) {
        let promoCodeLoans = [];
        formValues.promoCodeLoans.forEach(resp => {
          promoCodeLoans.push({
            loanId: resp,
            businessName: getLoanDetails(resp)?.businessName,
            loanStatus: getLoanDetails(resp)?.loanStatus,
            id: getLoanId(resp)?.id
          });
        });
        formValues.promoCodeLoans = promoCodeLoans;
      }
      formValues.promoCodeRanges.forEach(resp => {
        resp.value = resp.value ? formValues.type === CouponType.Percentage ? (parseFloat(resp.value) / 100) : resp.value : 0;
        resp.maximumCashbackAmount = formValues.type === CouponType.Percentage ? resp.maximumCashbackAmount : null;
      });
      if (formValues.loanApplicable === OpportinityType.All) {
        formValues.promoCodeLoans = [];
      }
      if (id) {
        onUpdateCode(formValues);
      } else {
        onSaveCode(formValues);
      }
    }
  };

  const getFundraisingLoans = () => {
    return loanService.getLoans(LoanStatuses.Fundraising, null, 1, 9999);
  };

  const checkError = () => {
    let values = form.values;
    setMaxCashbackError(false);
    if (values?.type === CouponType.Percentage) {
      // if (values.promoCodeRanges.some(item => parseFloat(item.value) > 100)) {
      //   Toaster({ msg: 'Cashback should be less than 100% when the promo code type is Percentage' });
      //   return true;
      // }
      if (values.promoCodeRanges.some(item => !item?.maximumCashbackAmount)) {
        setMaxCashbackError(true);
        return true;
      }
      return false;
    }
    return false;
  };

  const onNavigate = () => {
    navigate(buildRegionizeRoute(`${AppRoutes.PromotionalCode}`));
  };

  const onUpdateCode = (formValues) => {
    delete formValues.loanApplicable;
    setLoading(true);
    promotionalCodeService.updateCode(codeDetail?.id, formValues).then(resp => {
      setLoading(false);
      onNavigate();
    }).catch(err => {
      // form.setFieldValue("promoCodeLoans", []);
      setLoading(false);
    });
  };

  const onSaveCode = (formValues) => {
    delete formValues.loanApplicable;
    setLoading(true);
    promotionalCodeService.addCode(formValues).then(resp => {
      setLoading(false);
      onNavigate();
    }).catch(err => {
      // form.setFieldValue("promoCodeLoans", []);
      setLoading(false);
    });
  };

  const removeDiscount = (index) => {
    const updatedDiscounts = form.values.promoCodeRanges.filter((_, i) => i !== index);
    form.setFieldValue("promoCodeRanges", updatedDiscounts);
  };

  const addDiscount = () => {
    const newDiscount = { toAmount: null, fromAmount: null, value: null, maximumCashbackAmount: null };
    form.setFieldValue("promoCodeRanges", [
      ...form.values.promoCodeRanges,
      newDiscount
    ]);
  };

  const isPercentage = () => form.values.type === CouponType.Percentage;

  const deletButton = (index) => {
    return form.values.promoCodeRanges?.length >= 2 ? <ButtonWrapper onClick={() => {
      removeDiscount(index);
    }} classList={'!top-1 !relative !ml-3'} iconButton icon={<DeleteOutlineIcon color='error' />} /> : null;
  };

  return <Box>
    <FormikProvider value={form}>
      <ActionBarDetail
        loading={loading}
        userName={`${codeDetail?.id ? codeDetail?.code : 'Create Promo Code'}`}></ActionBarDetail>
      <Spacing></Spacing>
      <SectionWithHeading loading={loading} actionButtons={
        <Box className='flex flex-row space-x-2'>
          <ButtonWrapper
            icon={<SaveAsIcon></SaveAsIcon>}
            classList="!bg-red-500"
            label="Save as draft"
            onClick={(e) => {
              form.setFieldValue('isActive', false);
              form.submitForm();
            }}></ButtonWrapper>
          <ButtonWrapper
            icon={<PublishIcon></PublishIcon>}
            label="Save & Publish"
            onClick={(e) => {
              form.setFieldValue('isActive', true);
              form.submitForm();
            }}></ButtonWrapper>
        </Box>

      }
        isEditToggle={true} heading={'Promo Code Details'}>
        <Box className='flex flex-col space-y-3'>
          <Box className="grid grid-cols-4 gap-4">
            <InputWrapper key={'code'} name={'code'} label={'Promo Code Name'} />
            <SelectWrapper
              items={[
                { name: 'Fixed', displayName: 'Fixed', id: CouponType.Fixed },
                { name: 'Percentage', displayName: 'Percentage', id: CouponType.Percentage }
              ]}
              key={'type'}
              name={'type'}
              label={'Promo Code Type'} />
            <SelectWrapper
              items={ApplicableOpportinities}
              key={'loanApplicable'}
              name={'loanApplicable'}
              label={'Applicable Opportunities'} />
            {form.values?.loanApplicable === OpportinityType.Custom ? <SelectWrapper
              items={loans}
              multiSelect
              key={'promoCodeLoans'}
              name={'promoCodeLoans'}
              label={'Opportunities'} /> : null}
            <DatePickerWrapper key={'startDate'} name={'startDate'} label={'Start Date'} />
            <DatePickerWrapper key={'expiryDate'} name={'expiryDate'} label={'Expiry Date'} />
            <InputWrapper type='number' key={'maxTotalUsage'} name={'maxTotalUsage'} label={'Maximum Redemptions Allowed'} />
            <InputWrapper type='number' key={'maxUsagePerUser'} name={'maxUsagePerUser'} label={'Maximum Redemptions Allowed per User'} />
          </Box>
          <Box>
            <Box className='flex justify-between items-center'>
              <Box className='font-[500] !text-md'>Promo Code Ranges</Box>
              <ButtonWrapper onClick={() => addDiscount()} iconButton icon={<AddIcon />} />
            </Box>
            {form.values.promoCodeRanges && form.values?.promoCodeRanges?.length ? <Box className='flex flex-col space-y-2'>
              {form.values.promoCodeRanges.map((resp, index) => {
                return <Box key={index} className={`grid gap-4 mt-2 ${isPercentage() ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  <InputWrapper
                    isCurrencyText
                    label={`From Investment Amount (${CURRENCY})`}
                    name={`promoCodeRanges[${index}].fromAmount`}
                    key={`promoCodeRanges[${index}].fromAmount`} />
                  <InputWrapper
                    isCurrencyText
                    label={`To Investment Amount (${CURRENCY})`}
                    name={`promoCodeRanges[${index}].toAmount`}
                    key={`promoCodeRanges[${index}].toAmount`} />
                  <Box className='flex items-center relative'>
                    <Box className='flex flex-col flex-1'>
                      <small className='caption text-[11px]' style={{ letterSpacing: "0.03333em" }}>
                        {`Cashback Value ${isPercentage() ? '(%)' : `(${CURRENCY})`}`}
                      </small>
                      <InputWrapper
                        type='number'
                        name={`promoCodeRanges[${index}].value`}
                        key={`promoCodeRanges[${index}].value`} />
                    </Box>
                    {!isPercentage() ? deletButton(index) : null}
                  </Box>
                  {isPercentage() ?
                    <Box className={`flex items-center relative ${maxCashbackError && !form.values.promoCodeRanges[index].maximumCashbackAmount ? 'cashback-error' : ''}`}>
                      <Box className='flex flex-col flex-1'>
                        <InputWrapper onChanges={(e) => {
                          if (!e) {
                            setMaxCashbackError(true);
                            return;
                          }
                          setMaxCashbackError(false);
                        }}
                          isCurrencyText
                          label={`Maximum Cashback Amount (${CURRENCY})`}
                          name={`promoCodeRanges[${index}].maximumCashbackAmount`}
                          key={`promoCodeRanges[${index}].maximumCashbackAmount`} />
                        {maxCashbackError && !form.values.promoCodeRanges[index].maximumCashbackAmount ? <small className='Mui-error'>This is a mandatory field</small> : null}
                      </Box>
                      {deletButton(index)}
                    </Box>
                    : null}
                </Box>;
              })}
            </Box> : null}
          </Box>
        </Box>
      </SectionWithHeading>
    </FormikProvider>
  </Box>;
};